import { Component, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';

/**
 * DtisFormDialogComponent
 *
 * A reusable dialog wrapper component for form-based dialogs.
 * Provides a standard Material Dialog layout with a title, content area,
 * and action buttons (cancel, save, optional delete).
 *
 * Uses content projection (`<ng-content>`) for the form body,
 * allowing consumers to provide their own form fields.
 *
 * Designed to be used inside a `MatDialog` context but also works
 * standalone with the Material Dialog structural directives for styling.
 *
 * @example
 * ```html
 * <dtis-form-dialog
 *   [title]="'edit-item'"
 *   [saveDisabled]="!form.valid"
 *   [isLoading]="isLoading()"
 *   (onSave)="handleSave()"
 *   (onCancel)="handleCancel()"
 * >
 *   <form [formGroup]="myForm">
 *     <!-- form fields -->
 *   </form>
 * </dtis-form-dialog>
 * ```
 */
@Component({
  selector: 'dtis-form-dialog',
  template: `
    <h2 mat-dialog-title>{{ title() | transloco }}</h2>
    <mat-dialog-content>
      <div class="py-4">
        <ng-content />
      </div>
    </mat-dialog-content>
    <mat-dialog-actions class="!flex !justify-between gap-2">
      @if (showDelete()) {
        <button
          mat-button
          (click)="deleted.emit()"
          [disabled]="deleteDisabled()"
        >
          {{ deleteLabel() | transloco }}
        </button>
      } @else {
        <div></div>
      }
      <div class="flex gap-2">
        <button mat-button (click)="cancelled.emit()">
          {{ cancelLabel() | transloco }}
        </button>
        <button
          mat-flat-button
          (click)="saved.emit()"
          [disabled]="saveDisabled() || isLoading()"
        >
          @if (isLoading()) {
            <mat-spinner diameter="20" />
          }
          {{ saveLabel() | transloco }}
        </button>
      </div>
    </mat-dialog-actions>
  `,
  host: { class: 'flex w-full flex-col' },
  imports: [MatDialogModule, MatButtonModule, MatProgressSpinnerModule, TranslocoModule],
})
export class DtisFormDialogComponent {
  /** Translation key or text for the dialog title */
  readonly title = input.required<string>();

  /** Translation key for the save button label */
  readonly saveLabel = input('save');

  /** Translation key for the cancel button label */
  readonly cancelLabel = input('cancel');

  /** Translation key for the delete button label */
  readonly deleteLabel = input('delete');

  /** Whether to show the delete button */
  readonly showDelete = input(false);

  /** Whether the save button should be disabled */
  readonly saveDisabled = input(false);

  /** Whether the delete button should be disabled */
  readonly deleteDisabled = input(false);

  /** Whether the dialog is in a loading state */
  readonly isLoading = input(false);

  /** Emitted when the save button is clicked */
  readonly saved = output<void>();

  /** Emitted when the cancel button is clicked */
  readonly cancelled = output<void>();

  /** Emitted when the delete button is clicked */
  readonly deleted = output<void>();
}
